import type { MouseEvent } from 'react';
import { getMarketingPayloadForLoginPost, getShareRefForLogin, SHARE_REF_PARAM } from '@/lib/marketingContextStorage';

const VALID_IDPS = ['auth0', 'demo', 'entra'] as const;

declare global {
    interface Window {
        Cypress?: unknown;
    }
}

/**
 * Returns the IdP hint from sessionStorage key `cypress_idp` only when running inside Cypress.
 * Cypress sets sessionStorage.cypress_idp=auth0|demo|entra in e2e; production never sets it.
 */
function getCypressIdpFromSession(): string {
    if (typeof window === 'undefined') return '';
    if (!window.Cypress) return '';
    try {
        const raw = window.sessionStorage.getItem('cypress_idp');
        const idp = raw?.toLowerCase() ?? '';
        return VALID_IDPS.includes(idp as (typeof VALID_IDPS)[number]) ? idp : '';
    }
    catch {
        return '';
    }
}

/**
 * When on localhost (e.g. Vite dev-engineer on :5173), append redirect_origin so the API
 * redirects back to this origin after Auth0 callback instead of the proxied backend host.
 */
function appendRedirectOriginIfLocalhost(url: string): string {
    if (typeof window === 'undefined') return url;
    const origin = window.location.origin;
    if (!origin || !origin.startsWith('http://localhost')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}redirect_origin=${encodeURIComponent(origin)}`;
}

/**
 * Returns the login URL for redirecting to sign-in.
 * When running in Cypress and sessionStorage.cypress_idp is set, appends ?idp= so the API
 * (when CYPRESS_TESTING is set) uses that IdP.
 */
export function getLoginUrl(): string {
    const idp = getCypressIdpFromSession();
    const base = idp ? `/api/login?idp=${idp}` : '/api/login';
    return appendRedirectOriginIfLocalhost(base);
}

/**
 * Returns the login URL with an Auth0 connection hint (e.g. 'google-oauth2') so the user is sent directly to that IdP.
 * Used when we know the account is associated with a specific connection (e.g. from change-password-migration).
 */
export function getLoginUrlWithConnection(connection: string): string {
    const base = getLoginUrl();
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}connection=${encodeURIComponent(connection)}`;
}

/**
 * Returns the get-started URL. When running in Cypress and sessionStorage.cypress_idp is set, appends ?idp= for API hint.
 * When on localhost, appends redirect_origin so callback returns to this origin (e.g. start:local:dev-engineer).
 */
export function getGetStartedUrl(): string {
    const idp = getCypressIdpFromSession();
    const base = idp ? `/api/get-started?idp=${idp}` : '/api/get-started';
    return appendRedirectOriginIfLocalhost(base);
}

function appendMarketingSearchParams(
    path: string,
    marketing: ReturnType<typeof getMarketingPayloadForLoginPost>,
    shareRef: string | undefined = getShareRefForLogin(),
): string {
    if (!marketing) {
        return path;
    }
    const extra = new URLSearchParams();
    for (const [key, value] of Object.entries(marketing)) {
        if (typeof value === 'string' && value.length > 0) {
            extra.set(key, value);
        }
    }
    if (shareRef) {
        extra.set(SHARE_REF_PARAM, shareRef);
    }
    const fragment = extra.toString();
    if (!fragment) {
        return path;
    }
    return path.includes('?') ? `${path}&${fragment}` : `${path}?${fragment}`;
}

/** Full-page GET to `/api/login` or `/api/get-started` with optional `utm_*` query params from sessionStorage + current URL. */
function navigateToAuthApiPath(path: string): void {
    const marketing = getMarketingPayloadForLoginPost();
    const pathWithMarketing = appendMarketingSearchParams(path, marketing);
    window.location.href = pathWithMarketing;
}

/**
 * Navigate to the login URL with an optional Auth0 connection hint (e.g. 'google-oauth2').
 * When connection is provided, the user is sent directly to that IdP.
 */
export function navigateToLogin(connection?: string): void {
    const path = connection ? getLoginUrlWithConnection(connection) : getLoginUrl();
    navigateToAuthApiPath(path);
}

/** Full-page GET to `/api/logout`, which ends the session (if any) and redirects. */
export function navigateToLogout(): void {
    window.location.href = '/api/logout';
}

/** Navigate to the get-started URL (GET /api/get-started) with optional marketing query params. */
export function navigateToGetStarted(): void {
    const path = getGetStartedUrl();
    navigateToAuthApiPath(path);
}

/**
 * Get-started URL with `utm_*` params already appended, for use as a real anchor `href` so
 * modifier-clicks and middle-clicks (which bypass onClick handlers) still carry attribution.
 */
export function getGetStartedUrlWithMarketing(): string {
    return appendMarketingSearchParams(getGetStartedUrl(), getMarketingPayloadForLoginPost());
}

/** Login URL with `utm_*` params already appended; see {@link getGetStartedUrlWithMarketing}. */
export function getLoginUrlWithMarketing(): string {
    return appendMarketingSearchParams(getLoginUrl(), getMarketingPayloadForLoginPost());
}

/**
 * True when a click should be left to the browser's default action (e.g. open in a new tab)
 * instead of intercepted for a client-side redirect — modifier-clicks and non-primary-button
 * clicks on a real `<a>` conventionally bypass app-level click handlers this way.
 */
export function isModifiedClick(event: MouseEvent): boolean {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}
